import { chunk } from 'lodash'
import {
  CREATE_LIST_ITEM_MUTATION,
  DELETE_LIST_ITEM_MUTATION,
  LIST_ITEMS_QUERY,
  LIST_MEMBERS_QUERY,
  LIST_STAGE_COUNTS_QUERY,
  UPDATE_LIST_ITEM_MUTATION,
} from 'app/graphql/lists'
import type { BulkProgressReporter, BulkRowOutcome } from 'app/components/table/bulkActions/types'
import { SortDirection, type SortInput } from 'app/generated/graphql'
import { gqlFetchList, gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import type { ContactJson } from 'app/types/entities'
import { announceListWrite } from 'lib/listWrites'
import { logError } from 'lib/logging'
import { listItemId } from 'lib/practiceLists'

// Callers own the user-facing message for a failed list call, so the global error toast stays off.
const QUIET = { suppressErrorToast: true } as const

// No list write returns a typename these reads cache (ListMembersResult, ListStageCount), so urql would never
// invalidate them. They must hit the network or boards, paging and the stage-delete guard serve stale data.
const FRESH_READ = { requestPolicy: 'network-only', suppressErrorToast: true } as const

// Works on filter lists today; manual lists ignore it until TRK-5879 is fixed. Never sort client-side to compensate.
export const LIST_SORT: Readonly<{ field: string; direction: SortDirection }> = {
  field: 'name.plain',
  direction: SortDirection.Asc,
}

export async function readListPage(args: {
  listId: string
  stageId?: string
  search?: string
  sort?: SortInput
  page: number
  limit: number
}): Promise<{ contacts: ContactJson[]; total: number; stageByEntity: Map<string, string | undefined> }> {
  const { data, error } = await gqlQuery(
    LIST_MEMBERS_QUERY,
    {
      listId: args.listId,
      stageId: args.stageId,
      search: args.search,
      pagination: { page: args.page, limit: args.limit },
      sort: args.sort ?? LIST_SORT,
    },
    FRESH_READ,
  )
  if (error || !data) throw new Error(error?.message ?? 'List members came back empty')
  const { members, total } = data.listMembers
  const stageByEntity = new Map<string, string | undefined>(
    members.map((member) => [member.entityId, member.stageId ?? undefined]),
  )
  if (members.length === 0 || args.limit === 0) return { contacts: [], total, stageByEntity }

  const ids = members.map((member) => member.entityId)
  const { hits } = await gqlFetchList<ContactJson>(
    'contacts',
    { search: `id:(${ids.map((id) => `"${id}"`).join(' OR ')})`, pagination: { page: 0, limit: ids.length } },
    undefined,
    { throwOnError: true, suppressErrorToast: true },
  )
  const byId = new Map(hits.map((contact) => [contact.id, contact]))
  const contacts = ids.flatMap((id) => byId.get(id) ?? [])
  // A deleted contact can keep its list row, so a member may have no contact to draw.
  if (contacts.length < ids.length) {
    const missing = ids.filter((id) => !byId.has(id))
    logError('List members point at contacts that were not found', new Error(missing.join(', ')))
  }
  return { contacts, total, stageByEntity }
}

export async function readStageCounts(listId: string, search?: string): Promise<Map<string, number>> {
  const { data, error } = await gqlQuery(LIST_STAGE_COUNTS_QUERY, { listId, search }, FRESH_READ)
  if (error || !data) throw new Error(error?.message ?? 'Stage counts came back empty')
  return new Map(data.listStageCounts.map((row) => [row.stageId, row.count]))
}

// Larger than any page of contacts times the lists one contact can be on, so a read is one request in practice.
const ITEMS_PAGE_LIMIT = 1000

// Bounds one search clause; a bulk selection can name thousands of contacts.
const ITEMS_READ_ID_CHUNK = 500

type EntityListItem = { listId: string; entityId: string; stageId: string | undefined }

export async function readEntityListItems(entityIds: readonly string[]): Promise<EntityListItem[]> {
  const items: EntityListItem[] = []
  for (const ids of chunk(entityIds, ITEMS_READ_ID_CHUNK)) {
    const search = `entityId:(${ids.map((id) => `"${id}"`).join(' OR ')})`
    const read: EntityListItem[] = []
    for (let page = 0; ; page++) {
      const { data, error } = await gqlQuery(
        LIST_ITEMS_QUERY,
        { search, pagination: { page, limit: ITEMS_PAGE_LIMIT } },
        FRESH_READ,
      )
      if (error || !data) throw new Error(error?.message ?? 'List items came back empty')
      const { hits, total } = data.listItems
      read.push(
        ...hits.map((hit) => ({ listId: hit.listId, entityId: hit.entityId, stageId: hit.stageId ?? undefined })),
      )
      if (hits.length === 0 || read.length >= total) break
    }
    items.push(...read)
  }
  return items
}

// The API's message for a rule member that has no row until its first move. gqlMutation passes urql's
// CombinedError message through, which prefixes GraphQL errors with "[GraphQL] ".
const NO_ROW = `ListItem not found`
const GRAPHQL_PREFIX = `[GraphQL] `

// Update first: a move to an exit deletes the row on the server, so a create-then-update would fail
// its second call after the move had landed.
export async function moveListItem(listId: string, entityId: string, stageId: string): Promise<void> {
  const updated = await gqlMutation(
    UPDATE_LIST_ITEM_MUTATION,
    { id: listItemId(listId, entityId), input: { stageId } },
    QUIET,
  )
  if (updated.error) {
    if (updated.error.message.replace(GRAPHQL_PREFIX, ``) !== NO_ROW) throw new Error(updated.error.message)
    const created = await gqlMutation(CREATE_LIST_ITEM_MUTATION, { input: { listId, entityId, stageId } }, QUIET)
    if (created.error) throw new Error(created.error.message)
  }
  announceListWrite()
}

// One write per contact, so a bulk add or move of hundreds goes out a few at a time rather than all at once.
const WRITE_CONCURRENCY = 10

export async function addListItems(
  listId: string,
  entityIds: readonly string[],
  stageId: string,
): Promise<{ added: string[]; failed: string[] }> {
  const results: { entityId: string; ok: boolean }[] = []
  for (const ids of chunk(entityIds, WRITE_CONCURRENCY)) {
    results.push(
      ...(await Promise.all(
        ids.map(async (entityId) => {
          const { error } = await gqlMutation(
            CREATE_LIST_ITEM_MUTATION,
            { input: { listId, entityId, stageId } },
            QUIET,
          )
          return { entityId, ok: !error }
        }),
      )),
    )
  }
  const added = results.filter((result) => result.ok).map((result) => result.entityId)
  if (added.length > 0) announceListWrite()
  return { added, failed: results.filter((result) => !result.ok).map((result) => result.entityId) }
}

export async function readStageMemberIds(listId: string, stageId: string): Promise<string[]> {
  const ids: string[] = []
  for (let page = 0; ; page++) {
    const { data, error } = await gqlQuery(
      LIST_MEMBERS_QUERY,
      { listId, stageId, pagination: { page, limit: ITEMS_READ_ID_CHUNK }, sort: LIST_SORT },
      FRESH_READ,
    )
    if (error || !data) throw new Error(error?.message ?? 'List members came back empty')
    const { members, total } = data.listMembers
    ids.push(...members.map((member) => member.entityId))
    if (members.length === 0 || ids.length >= total) return ids
  }
}

/** A stage's own rows, without the rule matches `listMembers` files under the default stage. */
export async function readStageRowIds(listId: string, stageId: string): Promise<string[]> {
  const search = `listId:"${listId}" AND stageId:"${stageId}"`
  const ids: string[] = []
  for (let page = 0; ; page++) {
    const { data, error } = await gqlQuery(
      LIST_ITEMS_QUERY,
      { search, pagination: { page, limit: ITEMS_PAGE_LIMIT } },
      FRESH_READ,
    )
    if (error || !data) throw new Error(error?.message ?? 'List items came back empty')
    const { hits, total } = data.listItems
    ids.push(...hits.map((hit) => hit.entityId))
    if (hits.length === 0 || ids.length >= total) return ids
  }
}

/** Moves a stage's members to another stage, a few at a time, reporting each one as it lands. */
export async function moveStageMembers(
  listId: string,
  members: readonly { entityId: string; label: string }[],
  toStageId: string,
  onProgress?: BulkProgressReporter,
): Promise<BulkRowOutcome[]> {
  const outcomes: BulkRowOutcome[] = []
  for (const batch of chunk(members, WRITE_CONCURRENCY)) {
    const landed = await Promise.all(
      batch.map(async ({ entityId, label }): Promise<BulkRowOutcome> => {
        try {
          await moveListItem(listId, entityId, toStageId)
          return { id: entityId, label, ok: true }
        } catch (err) {
          logError(`Error moving a contact out of a stage being deleted`, entityId, err)
          return { id: entityId, label, ok: false, reason: err instanceof Error ? err.message : String(err) }
        }
      }),
    )
    for (const outcome of landed) {
      outcomes.push(outcome)
      const failed = outcomes.filter((row) => !row.ok).length
      onProgress?.({
        done: outcomes.length,
        total: members.length,
        ok: outcomes.length - failed,
        failed,
        skipped: 0,
        last: outcome,
      })
    }
  }
  return outcomes
}

export async function removeListItem(listId: string, entityId: string): Promise<void> {
  const { data, error } = await gqlMutation(DELETE_LIST_ITEM_MUTATION, { id: listItemId(listId, entityId) }, QUIET)
  if (error) throw new Error(error.message)
  if (!data?.deleteListItem) throw new Error('The list item was not deleted')
  announceListWrite()
}
